import {
  PlayerReadiness, UserSessionState, type PlayerState
} from "../../generated/bay_session_shared_pb";

/**
 * confirmationPromptOwed — does this PlayerState frame mean "the player must
 * tap to confirm before they can swing"? The confirmation controller uses it
 * as the bay-session visibility input.
 *
 * TWO independent server gates can owe a confirmation, and each is cleared by
 * a DIFFERENT RPC (the modal routes between them on readiness — SMG-3967):
 *
 *   per-TURN gate — `PlayerState.awaiting_confirmation`
 *     (playersnap.subjectAwaitingConfirmation): the subject is the active
 *     player AND the station's turn gate is armed. That gate is armed
 *     EXCLUSIVELY from the player's `confirm_on_turn` preference
 *     (bay-session armAwaitingConfirmationForPlayer), which joins at an
 *     explicit FALSE default. Cleared by RespondToConfirmation.
 *
 *   readiness gate — `SessionPlayer.readiness == CONFIRM`
 *     (sessionsnap.derivePlayerReadiness): GUARDIAN_REQUIRED or the opted-in
 *     walkaway lock is engaged. ConfirmReadyToSwing clears self-clearable
 *     lock_status values; guardian authorization remains a separate flow.
 *     A simulator-owned timeout is PAUSED + NOT_ACTIVE and never enters this
 *     player-gesture flow.
 *
 * SMG-3970: the modal used to key off the per-TURN gate alone, so the cohort
 * that has walkaway protection ON but the per-turn toggle at its OFF default
 * — exactly what QA hit on the legacy FS Golf path — was locked out with no
 * prompt at all: D3 rejected their shots and nothing on screen could clear
 * the lock. SMG-3967 had already taught the modal to CLEAR a lock once
 * visible; nothing put it on screen for one.
 *
 * The lock arm is additionally gated on the subject being the ACTIVE player,
 * which mirrors the per-turn gate's own `is_active` precondition and keeps
 * the prompt where the player can act on it:
 *
 *   - A benched player cannot swing anyway, so prompting them would be noise
 *     and the lock would just re-assert. They are prompted the moment the
 *     simulator (or rotation) hands them the turn.
 *   - It also keeps the modal's follow-up RespondToConfirmation on its
 *     supported path. In ROTATION that RPC rejects a non-active caller
 *     (ErrNotCurrentRotationPlayer); for the active player it is a no-op
 *     success when no turn is pending.
 *
 * Junior/COPPA note: `GUARDIAN_REQUIRED` also derives readiness CONFIRM, and
 * ConfirmReadyToSwing deliberately will NOT self-clear it (only
 * RecordGuardianAcknowledgement can). That path is a lenient server-side
 * no-op, not an error, so a Junior sees the prompt, taps, and the guardian
 * flow remains the real gate — no stuck state and no error toast.
 */
export function confirmationPromptOwed(state: PlayerState | undefined): boolean {
  if (!state) return false;

  // Per-turn gate. Already carries its own is-active precondition server-side.
  if (state.awaitingConfirmation) return true;

  // Readiness gate on the player whose turn it is.
  return state.userSessionState === UserSessionState.ACTIVE && state.sessionPlayer?.readiness === PlayerReadiness.CONFIRM;
}
